<script setup lang="ts">
/**
 * MustChangePassword — the screen that stands in front of everything while an
 * account still carries a temporary password (Tom, 2026-10-08, job #236).
 *
 * Mounted once in App.vue, so it covers the player, the schools shell and every
 * other route the same way. No close, no skip: the only ways out are choosing a
 * password (api/auth/set-new-password clears the flag in the same call) or
 * signing out. After saving it signs in again with the new password, because the
 * change ends the old session, so the user object loses the flag and the overlay lets go.
 */
import { computed, inject, ref } from 'vue'
import type { SupabaseClient } from '@supabase/supabase-js'
import { mustChangePassword } from '@/auth/mustChangePassword'
import { MIN_PASSWORD_LENGTH } from '@/composables/useManagerOnboarding'
import { useI18n } from '@/composables/useI18n'
import PasswordReveal from '@/components/auth/PasswordReveal.vue'

const props = defineProps<{ client: SupabaseClient | null }>()
const { t } = useI18n()
const auth = inject<any>('auth', null)

const open = computed(() => mustChangePassword(auth?.user?.value))
const email = computed<string>(() => auth?.user?.value?.email ?? '')

const password = ref('')
const again = ref('')
const error = ref('')
const saving = ref(false)

async function save(): Promise<void> {
  if (saving.value || !props.client) return
  if (password.value.length < MIN_PASSWORD_LENGTH) {
    error.value = t('temporaryPassword.tooShort', 'Password must be at least {n} characters.').replace('{n}', String(MIN_PASSWORD_LENGTH))
    return
  }
  if (password.value !== again.value) {
    error.value = t('temporaryPassword.mismatch', 'The two passwords do not match.')
    return
  }
  error.value = ''
  // Captured before the first await: the field stays editable while the save is in flight,
  // and the sign-in must use exactly the password the server was given.
  const chosen = password.value
  const accountEmail = email.value
  saving.value = true
  try {
    const { data } = await props.client.auth.getSession()
    const token = data?.session?.access_token
    const res = await fetch('/api/auth/set-new-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ password: chosen }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      error.value = body?.error || t('temporaryPassword.failed', 'Could not save that password. Try again.')
      return
    }
    // Setting the password through the admin API ends every session the account
    // holds, so a refresh is refused (live 2026-10-08: 400, and the schools shell
    // read "Missing or invalid Authorization header"). Sign in again with the new
    // password: a fresh session whose user record no longer carries the flag.
    const { error: signInError } = await props.client.auth.signInWithPassword({ email: accountEmail, password: chosen })
    if (signInError) {
      // Password is saved; a clean sign-in screen beats a shell with a dead session.
      await auth?.signOut?.()
      return
    }
    // Views behind the overlay fetched during the swap with the dead token and
    // show "Your session has ended" (live 2026-10-08). One reload, fresh session.
    window.location.reload()
    password.value = ''
    again.value = ''
  } catch {
    error.value = t('temporaryPassword.failed', 'Could not save that password. Try again.')
  } finally {
    saving.value = false
  }
}

async function signOut(): Promise<void> {
  await auth?.signOut?.()
}
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="mcp-overlay" data-testid="must-change-password">
      <form class="mcp-card" role="dialog" aria-modal="true" :aria-label="t('temporaryPassword.title', 'Choose your own password')" @submit.prevent="save">
        <h2 class="mcp-title">{{ t('temporaryPassword.title', 'Choose your own password') }}</h2>
        <p class="mcp-body">{{ t('temporaryPassword.body', 'You signed in with a temporary password. Choose a password of your own now — you will use it every time from here on.') }}</p>
        <label class="mcp-label" for="mcp-username">{{ t('temporaryPassword.account', 'Your account') }}</label>
        <input id="mcp-username" :value="email" type="email" class="mcp-input" autocomplete="username" readonly tabindex="-1" />
        <label class="mcp-label" for="mcp-password">{{ t('temporaryPassword.newPassword', 'New password') }}</label>
        <PasswordReveal v-slot="{ type }"><input id="mcp-password" v-model="password" :type="type" class="mcp-input" autocomplete="new-password" /></PasswordReveal>
        <label class="mcp-label" for="mcp-again">{{ t('temporaryPassword.confirm', 'Type it again') }}</label>
        <PasswordReveal v-slot="{ type }"><input id="mcp-again" v-model="again" :type="type" class="mcp-input" autocomplete="new-password" /></PasswordReveal>
        <p v-if="error" class="mcp-error" role="alert">{{ error }}</p>
        <button type="submit" class="mcp-save" :disabled="saving || !password || !again">
          {{ saving ? t('temporaryPassword.saving', 'Saving…') : t('temporaryPassword.save', 'Save and continue') }}
        </button>
        <button type="button" class="mcp-out" @click="signOut">{{ t('temporaryPassword.signOut', 'Sign out') }}</button>
      </form>
    </div>
  </Teleport>
</template>

<style scoped>
.mcp-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary, #e8e3dd);
  padding: max(1rem, env(safe-area-inset-top)) max(1rem, env(safe-area-inset-right)) max(1rem, env(safe-area-inset-bottom)) max(1rem, env(safe-area-inset-left));
}
.mcp-card {
  width: 100%;
  max-width: 26rem;
  background: #fff;
  border-radius: 14px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}
.mcp-title { margin: 0; font-size: 1.25rem; }
.mcp-body { margin: 0 0 0.5rem; line-height: 1.45; }
.mcp-label { font-size: 0.8125rem; opacity: 0.75; margin-top: 0.25rem; }
.mcp-input { font: inherit; padding: 0.65rem 0.75rem; border: 1px solid #c9c3bb; border-radius: 8px; }
.mcp-error { color: #b3261e; margin: 0.25rem 0 0; font-size: 0.9rem; }
.mcp-save { margin-top: 0.75rem; font: inherit; padding: 0.75rem; border: 0; border-radius: 10px; background: var(--accent, #2d2a26); color: #fff; cursor: pointer; }
.mcp-save:disabled { opacity: 0.5; cursor: default; }
.mcp-out { font: inherit; background: none; border: 0; opacity: 0.7; cursor: pointer; padding: 0.5rem; }
</style>
